import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../AxiosInstance';
import { trackReorderCandidatesViewed, trackReorderMarkedFulfilled } from '../services/analytics';
import { Toast, SearchBar, LoadingSpinner, EmptyState, Modal, BtnPrimary, BtnSecondary } from './v2-utils';
import '../styles/v2.css';

function formatProductName(item) {
  let s = item.productName || '';
  const detail = [item.brand, item.variety].filter(Boolean).join(' / ');
  if (detail) s += ` (${detail})`;
  return s;
}

function formatSupplier(item) {
  if (!item.supplierName) return '—';
  return item.supplierMarketName
    ? `${item.supplierName} (${item.supplierMarketName})`
    : item.supplierName;
}

export default function ViewReordersV2() {
  const [candidates, setCandidates]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selectedMap, setSelectedMap] = useState({});
  const [orderPrices, setOrderPrices] = useState({});
  const [search, setSearch]           = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fulfilling, setFulfilling]   = useState(false);
  const [toast, setToast]             = useState(null);
  const [isDesktop, setIsDesktop]     = useState(window.innerWidth >= 900);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    axiosInstance.get('/inventories/reorders/open')
      .then(res => {
        const items = Array.isArray(res.data?.openReorders) ? res.data.openReorders : [];
        const initSel = {}, initPrices = {};
        items.forEach(item => {
          initSel[item.reorderId] = false;
          initPrices[item.reorderId] = item.orderPrice ?? 0;
        });
        setCandidates(items);
        setSelectedMap(initSel);
        setOrderPrices(initPrices);
        trackReorderCandidatesViewed();
      })
      .catch(() => setToast({ message: 'Failed to load open reorders.', severity: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const supplierOptions = useMemo(() => {
    const map = new Map();
    candidates.forEach(item => {
      if (!item.supplierId) return;
      if (!map.has(item.supplierId)) map.set(item.supplierId, formatSupplier(item));
    });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [candidates]);

  const filtered = candidates.filter(item => {
    const q = search.toLowerCase().trim();
    const matchSearch = !q || `${item.productName || ''} ${item.brand || ''} ${item.variety || ''}`.toLowerCase().includes(q);
    const matchSupplier = !supplierFilter || String(item.supplierId) === String(supplierFilter);
    return matchSearch && matchSupplier;
  });

  const allSelected  = filtered.length > 0 && filtered.every(item => !!selectedMap[item.reorderId]);
  const someSelected = filtered.some(item => !!selectedMap[item.reorderId]);

  const toggleAll = () => {
    setSelectedMap(prev => {
      const next = { ...prev };
      filtered.forEach(item => { next[item.reorderId] = !allSelected; });
      return next;
    });
  };

  const selectedItems = candidates.filter(item => !!selectedMap[item.reorderId]);

  const handleFulfillClick = () => {
    if (!selectedItems.length) { setToast({ message: 'Select at least one item to fulfill.', severity: 'error' }); return; }
    setConfirmOpen(true);
  };

  const handleConfirmFulfill = async () => {
    setFulfilling(true);
    try {
      const reorderIds = selectedItems.map(item => item.reorderId);
      const orderPriceUpdates = {};
      selectedItems.forEach(item => {
        const original = item.orderPrice != null ? Number(item.orderPrice) : 0;
        const current  = Number(orderPrices[item.reorderId]);
        if (!isNaN(current) && current !== original) orderPriceUpdates[item.reorderId] = current;
      });
      await axiosInstance.post('/inventories/reorders/mark-fulfilled', { reorderIds, orderPrices: orderPriceUpdates });
      trackReorderMarkedFulfilled(null);
      setCandidates(prev => prev.filter(item => !reorderIds.includes(item.reorderId)));
      setSelectedMap(prev => { const next = { ...prev }; reorderIds.forEach(id => delete next[id]); return next; });
      setToast({ message: 'Selected reorders marked as fulfilled.', severity: 'success' });
      setConfirmOpen(false);
    } catch (err) {
      console.error('Fulfill reorders error:', err.response ?? err);
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to mark reorders as fulfilled.';
      setToast({ message: msg, severity: 'error' });
      setConfirmOpen(false);
    } finally { setFulfilling(false); }
  };

  if (loading) return <LoadingSpinner message="Loading open reorders…" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ color: '#0f172a', fontSize: 20, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>View Reorders</h1>
          <p style={{ color: '#94a3b8', fontSize: 13, margin: '4px 0 0' }}>{filtered.length} open reorder{filtered.length !== 1 ? 's' : ''}{someSelected ? ` · ${selectedItems.length} selected` : ''}</p>
        </div>
        <button onClick={handleFulfillClick} disabled={!someSelected} className="v2-btn-submit"
          style={{ width: 'auto', padding: '10px 20px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg style={{ width: 15, height: 15 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          Fulfill Reorders
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Filter by product, brand, variety…" />
        </div>
        {supplierOptions.length > 0 && (
          <div style={{ position: 'relative', minWidth: 180 }}>
            <select className="v2-input" style={{ paddingRight: 36, appearance: 'none', cursor: 'pointer' }}
              value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)}>
              <option value="">All suppliers</option>
              {supplierOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <EmptyState message={search || supplierFilter ? 'No reorders match your filters.' : 'No open reorders. All caught up!'} />
      ) : isDesktop ? (

        /* ── Desktop: table ── */
        <div className="v2-card" style={{ overflow: 'hidden' }}>
          <table className="v2-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f1f5f1' }}>
                <th style={{ padding: '12px 16px', width: 40 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll}
                    style={{ accentColor: '#10b981', width: 16, height: 16, cursor: 'pointer' }} />
                </th>
                {['Product', 'Current Stock', 'Order Qty', 'Unit', 'Order Price (K)', 'Supplier'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: h === 'Product' ? 'left' : 'center', fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, i) => {
                const key = item.reorderId;
                return (
                  <tr key={key} style={{ borderBottom: i < filtered.length - 1 ? '1px solid #f8faf8' : 'none', background: selectedMap[key] ? '#f0fdf4' : 'white' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <input type="checkbox" checked={!!selectedMap[key]} onChange={() => setSelectedMap(p => ({ ...p, [key]: !p[key] }))}
                        style={{ accentColor: '#10b981', width: 16, height: 16, cursor: 'pointer' }} />
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#1e293b', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatProductName(item)}</p>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: 13, color: '#475569' }}>
                      {item.currentStock != null ? Number(item.currentStock).toFixed(2) : '—'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#10b981' }}>
                      {item.orderQuantity ?? '—'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: 13, color: '#475569' }}>
                      {item.unitType || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <input type="number" value={orderPrices[key] ?? 0}
                        onChange={e => setOrderPrices(p => ({ ...p, [key]: e.target.value }))}
                        style={{ width: 90, padding: '6px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, textAlign: 'center', outline: 'none', fontFamily: 'inherit' }}
                        onFocus={e => e.target.style.borderColor = '#10b981'}
                        onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                        min="0" step="0.01" />
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: 13, color: '#475569', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formatSupplier(item)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      ) : (

        /* ── Mobile: cards ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ accentColor: '#10b981', width: 16, height: 16 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Select all ({filtered.length})</span>
          </div>
          {filtered.map(item => {
            const key = item.reorderId;
            return (
              <div key={key} className="v2-card" style={{ padding: 18, overflow: 'hidden', background: selectedMap[key] ? '#f0fdf4' : 'white', border: selectedMap[key] ? '1.5px solid #a7f3d0' : '1.5px solid transparent' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                  <input type="checkbox" checked={!!selectedMap[key]} onChange={() => setSelectedMap(p => ({ ...p, [key]: !p[key] }))}
                    style={{ accentColor: '#10b981', width: 18, height: 18, cursor: 'pointer', marginTop: 2 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatProductName(item)}</p>
                    <p style={{ margin: '3px 0 0', fontSize: 12, color: '#94a3b8' }}>{formatSupplier(item)}</p>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  {[
                    { label: 'Current Stock', value: item.currentStock != null ? Number(item.currentStock).toFixed(2) : '—' },
                    { label: 'Order Qty', value: item.orderQuantity ?? '—', green: true },
                    { label: 'Unit', value: item.unitType || '—' },
                  ].map(f => (
                    <div key={f.label} style={{ padding: '8px 12px', borderRadius: 8, background: '#f8fafc', border: '1px solid #f1f5f1' }}>
                      <p style={{ margin: '0 0 2px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{f.label}</p>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: f.green ? '#10b981' : '#334155' }}>{f.value}</p>
                    </div>
                  ))}
                  <div style={{ padding: '8px 12px', borderRadius: 8, background: '#f8fafc', border: '1px solid #f1f5f1' }}>
                    <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Order Price (K)</p>
                    <input type="number" value={orderPrices[key] ?? 0}
                      onChange={e => setOrderPrices(p => ({ ...p, [key]: e.target.value }))}
                      style={{ width: '100%', padding: '4px 8px', borderRadius: 6, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                      onFocus={e => e.target.style.borderColor = '#10b981'}
                      onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                      min="0" step="0.01" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirm modal */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} title="Confirm Fulfill Reorders"
        footer={<>
          <BtnSecondary onClick={() => setConfirmOpen(false)}>Cancel</BtnSecondary>
          <BtnPrimary onClick={handleConfirmFulfill} disabled={fulfilling}>
            {fulfilling ? 'Marking…' : 'Mark Fulfilled'}
          </BtnPrimary>
        </>}>
        <p style={{ fontSize: 14, color: '#475569', margin: '0 0 16px' }}>Mark the following reorder{selectedItems.length !== 1 ? 's' : ''} as fulfilled?</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {selectedItems.map(item => (
            <div key={item.reorderId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 10, background: '#f8fafc', border: '1px solid #f1f5f1' }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{formatProductName(item)}</p>
              <span style={{ fontSize: 12, color: '#10b981', fontWeight: 700, flexShrink: 0, marginLeft: 12 }}>{item.orderQuantity} {item.unitType || ''}</span>
            </div>
          ))}
        </div>
      </Modal>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
